import React from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { FONT, SIZE, WEIGHT } from '../styles/foundation/typography'
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, withOpacity } from '../styles/foundation/colors'

// The D2C forward-shipment summary tab bar, reusable: a bordered strip of divider-separated
// cells, each an icon chip + label (+ optional count). The active cell gets a colored bottom
// border and a faint tint. Use this everywhere so tabs never look different across the panel.
//
// tabs: [{ value, label, icon, color?, count? }]   value = the active tab's `value`   onChange(value)
export default function SummaryTabs({ tabs = [], value, onChange, sx }) {
  return (
    <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden', ...sx }}>
      <Box sx={{ display: 'flex', overflowX: 'auto' }}>
        {tabs.map((t, i) => {
          const color = t.color || PRIMARY_BLUE
          const isActive = value === t.value
          const countDisplay = t.count == null
            ? null
            : (t.count > 99999 ? '99,999+' : (t.count?.toLocaleString?.() ?? '—'))
          return (
            <React.Fragment key={t.value}>
              {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />}
              <Box
                onClick={() => onChange(t.value)}
                sx={{
                  // grow to fill the bar when there are few tabs, but never shrink below the
                  // label's width — otherwise many long-label tabs overlap. Overflow scrolls.
                  flex: '1 0 auto', minWidth: 130, py: 1.25, px: 2, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                  borderBottom: isActive ? `3px solid ${color}` : '3px solid transparent',
                  bgcolor: isActive ? withOpacity(color, 0.06) : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: withOpacity(color, 0.04) },
                }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                  bgcolor: isActive ? withOpacity(color, 0.12) : withOpacity(color, 0.07),
                  color: isActive ? color : SUBTLE,
                  '& .MuiSvgIcon-root': { fontSize: 17 },
                }}>
                  {t.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium,
                    color: isActive ? NAVY : SUBTLE, lineHeight: 1.2, whiteSpace: 'nowrap',
                  }}>
                    {t.label}
                  </Typography>
                  {countDisplay != null && (
                    <Typography sx={{
                      fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold,
                      color: isActive ? color : NAVY, lineHeight: 1.3,
                    }}>
                      {countDisplay}
                    </Typography>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )
}
